import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Alert,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  NotificationsActive,
  Send,
  People,
  FilterList,
  Schedule,
  BarChart,
  History,
  Visibility,
  Delete,
  Edit,
  MyLocation,
  CampaignOutlined as Broadcast,
  Person,
  CheckCircle,
  Cancel
} from '@mui/icons-material';

interface Notification {
  title: string;
  body: string;
  imageUrl?: string;
}

interface NotificationData {
  type: string;
  actionUrl?: string;
  category?: string;
  deepLink?: string;
  campaign?: string;
}

interface NotificationFilters {
  gender?: 'all' | 'male' | 'female';
  minAge?: number;
  maxAge?: number;
  city?: string;
  isPremium?: boolean;
  lastActiveWithin?: number;
}

interface NotificationTemplate {
  title: string;
  body: string;
  type: string;
}

interface FilterOptions {
  cities: string[];
  ageRange: {
    min: number;
    max: number;
  };
  userCounts: {
    total: number;
    premium: number;
    free: number;
    male: number;
    female: number;
  };
}

interface NotificationStats {
  total: number;
  successful: number;
  failed: number;
  byType: {
    single: number;
    broadcast: number;
    filtered: number;
    scheduled: number;
  };
  byDay: Record<string, number>;
}

interface NotificationHistory {
  id: string;
  type: string;
  status: string;
  notification: Notification;
  targetUserId?: string;
  results?: any;
  sentAt: Date;
  sentBy: string;
  error?: string;
}

interface ScheduledNotification {
  id: string;
  type: string;
  status: string;
  notification: Notification;
  filters?: NotificationFilters;
  targetUserId?: string;
  scheduledFor: Date;
  createdAt: Date;
  createdBy: string;
  sentAt?: Date;
  result?: any;
  error?: string;
}

const NotificationsPage: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [notification, setNotification] = useState<Notification>({
    title: '',
    body: '',
    imageUrl: ''
  });
  const [notificationData, setNotificationData] = useState<NotificationData>({
    type: 'general'
  });
  const [filters, setFilters] = useState<NotificationFilters>({
    gender: 'all',
    minAge: 18,
    maxAge: 65
  });
  const [selectedUserId, setSelectedUserId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('12:00');
  const [isScheduled, setIsScheduled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Record<string, NotificationTemplate>>({});
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [scheduled, setScheduled] = useState<ScheduledNotification[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [estimatedReach, setEstimatedReach] = useState(0);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  // API calls
  const fetchTemplates = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/notifications/templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/notifications/filter-options', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setFilterOptions(data.data);
        setFilters(prev => ({
          ...prev,
          minAge: data.data.ageRange.min,
          maxAge: data.data.ageRange.max
        }));
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/notifications/stats?timeRange=30d', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/notifications/history?limit=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchScheduled = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/notifications/scheduled?status=scheduled', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setScheduled(data.data);
      }
    } catch (error) {
      console.error('Error fetching scheduled notifications:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/users?limit=10000&status=active', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        setUsers(data.data || []);
        console.log('Users loaded:', data.data?.length || 0);
      } else {
        console.error('Failed to fetch users:', data.message);
        setAlert({ type: 'error', message: 'Kullanıcılar yüklenemedi: ' + data.message });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlert({ type: 'error', message: 'Kullanıcılar yüklenirken hata oluştu' });
    }
  };

  // Calculate estimated reach
  const calculateEstimatedReach = React.useCallback(() => {
    if (!filterOptions) return;

    let reach = filterOptions.userCounts.total;

    if (filters.gender === 'male') {
      reach = filterOptions.userCounts.male;
    } else if (filters.gender === 'female') {
      reach = filterOptions.userCounts.female;
    }

    if (filters.isPremium === true) {
      reach = Math.min(reach, filterOptions.userCounts.premium);
    } else if (filters.isPremium === false) {
      reach = Math.min(reach, filterOptions.userCounts.free);
    }

    // Age and city filters would require more complex calculation
    // For now, we'll estimate a reduction
    if (filters.city) {
      reach = Math.floor(reach * 0.3); // Estimate 30% for specific city
    }

    setEstimatedReach(reach);
  }, [filterOptions, filters]);

  // Send notification functions
  const sendToUser = async () => {
    if (!selectedUserId || !notification.title || !notification.body) {
      setAlert({ type: 'error', message: 'Lütfen kullanıcı seçin ve bildirim içeriğini doldurun' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        userId: selectedUserId,
        notification,
        data: notificationData
      };

      if (isScheduled && scheduledDate) {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);

        await fetch('https://api.sobcof.com/api/notifications/schedule', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify({
            type: 'single',
            targetUserId: selectedUserId,
            notification,
            data: notificationData,
            scheduledFor: scheduledDateTime.toISOString()
          })
        });
        setAlert({ type: 'success', message: 'Bildirim başarıyla zamanlandı!' });
      } else {
        await fetch('https://api.sobcof.com/api/notifications/send-to-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify(payload)
        });
        setAlert({ type: 'success', message: 'Bildirim başarıyla gönderildi!' });
      }

      // Reset form
      setNotification({ title: '', body: '', imageUrl: '' });
      setSelectedUserId('');
      fetchHistory();
      fetchScheduled();
    } catch (error) {
      console.error('Error sending notification:', error);
      setAlert({ type: 'error', message: 'Bildirim gönderilirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const sendToAll = async () => {
    if (!notification.title || !notification.body) {
      setAlert({ type: 'error', message: 'Lütfen bildirim içeriğini doldurun' });
      return;
    }

    if (!window.confirm(`Toplam ${filterOptions?.userCounts.total || 0} kullanıcıya bildirim gönderilecek. Emin misiniz?`)) {
      return;
    }

    setLoading(true);
    try {
      // Get all active users IDs first
      // Assuming 'users' state contains all active users as fetched by fetchUsers()
      if (users.length === 0) {
        setAlert({ type: 'error', message: 'Hiçbir kullanıcı bulunamadı!' });
        setLoading(false);
        return;
      }

      // If scheduled, direct single request strictly for Vercel Cron usage context (if migrated later)
      // But for now, as per request, just batch send for immediate execution
      if (isScheduled && scheduledDate) {
        // Existing scheduling logic (unchanged)
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
        await fetch('https://api.sobcof.com/api/notifications/schedule', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify({
            type: 'broadcast',
            notification,
            data: notificationData,
            scheduledFor: scheduledDateTime.toISOString()
          })
        });
        setAlert({ type: 'success', message: 'Toplu bildirim başarıyla zamanlandı!' });
      } else {
        // Immediate Send - BATCHED
        const allUserIds = users.map(u => u.id);
        const batchSize = 200;
        let successCount = 0;
        let failedCount = 0;

        for (let i = 0; i < allUserIds.length; i += batchSize) {
          const batch = allUserIds.slice(i, i + batchSize);
          const batchNum = Math.floor(i / batchSize) + 1;
          const totalBatches = Math.ceil(allUserIds.length / batchSize);

          setAlert({ type: 'info', message: `Gönderiliyor... Paket ${batchNum}/${totalBatches}` });

          try {
            const response = await fetch('https://api.sobcof.com/api/notifications/send-multiple', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
              },
              body: JSON.stringify({
                userIds: batch,
                notification,
                data: notificationData
              })
            });

            if (response.ok) {
              successCount += batch.length; // Simply counting attempted as success for UI feedback
            } else {
              failedCount += batch.length;
              console.error(`Batch ${batchNum} failed`);
            }
          } catch (batchError) {
            console.error(`Error sending batch ${batchNum}:`, batchError);
            failedCount += batch.length;
          }
          // Small delay to be gentle
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        setAlert({
          type: successCount > 0 ? 'success' : 'error',
          message: failedCount > 0
            ? `İşlem tamamlandı. ${successCount} başarılı, ${failedCount} başarısız.`
            : `İşlem tamamlandı. Başarılı paketler gönderildi.`
        });
      }

      // Reset form
      setNotification({ title: '', body: '', imageUrl: '' });
      fetchHistory();
      fetchScheduled();
    } catch (error) {
      console.error('Error sending broadcast notification:', error);
      setAlert({ type: 'error', message: 'Toplu bildirim gönderilirken bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const sendFiltered = async () => {
    if (!notification.title || !notification.body) {
      setAlert({ type: 'error', message: 'Lütfen bildirim içeriğini doldurun' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        filters,
        notification,
        data: notificationData
      };

      if (isScheduled && scheduledDate) {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);

        await fetch('https://api.sobcof.com/api/notifications/schedule', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify({
            type: 'filtered',
            filters,
            notification,
            data: notificationData,
            scheduledFor: scheduledDateTime.toISOString()
          })
        });
        setAlert({ type: 'success', message: 'Filtreli bildirim başarıyla zamanlandı!' });
      } else {
        await fetch('https://api.sobcof.com/api/notifications/send-filtered', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          },
          body: JSON.stringify(payload)
        });
        setAlert({ type: 'success', message: 'Filtreli bildirim başarıyla gönderildi!' });
      }

      // Reset form
      setNotification({ title: '', body: '', imageUrl: '' });
      fetchHistory();
      fetchScheduled();
    } catch (error) {
      console.error('Error sending filtered notification:', error);
      setAlert({ type: 'error', message: 'Filtreli bildirim gönderilirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };



  const cancelScheduled = async (id: string) => {
    if (!window.confirm('Bu zamanlanmış bildirimi iptal etmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await fetch(`https://api.sobcof.com/api/notifications/scheduled/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setAlert({ type: 'success', message: 'Zamanlanmış bildirim iptal edildi' });
      fetchScheduled();
    } catch (error) {
      console.error('Error cancelling scheduled notification:', error);
      setAlert({ type: 'error', message: 'İptal işlemi sırasında hata oluştu' });
    }
  };

  const applyTemplate = (templateKey: string) => {
    const template = templates[templateKey];
    if (template) {
      setNotification({
        title: template.title,
        body: template.body,
        imageUrl: ''
      });
      setNotificationData({
        type: template.type
      });
    }
  };

  // Effects
  useEffect(() => {
    fetchTemplates();
    fetchFilterOptions();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchStats();
    } else if (activeTab === 2) {
      fetchHistory();
    } else if (activeTab === 3) {
      fetchScheduled();
    }
  }, [activeTab]);

  useEffect(() => {
    calculateEstimatedReach();
  }, [filters, filterOptions, calculateEstimatedReach]);

  // Auto-hide alerts
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Render functions
  const renderNotificationPreview = () => (
    <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
      <Typography variant="h6" gutterBottom>
        <Visibility sx={{ mr: 1 }} />
        Bildirim Önizlemesi
      </Typography>
      <Paper sx={{ p: 2, bgcolor: 'white', border: '1px solid', borderColor: 'grey.300' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
            <NotificationsActive />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {notification.title || 'Bildirim Başlığı'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {notification.body || 'Bildirim mesajı buraya gelecek...'}
            </Typography>
            {notification.imageUrl && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={notification.imageUrl}
                  alt="Bildirim görseli"
                  style={{ maxWidth: '100%', height: 'auto', maxHeight: 120, borderRadius: 4 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Paper>
  );

  const renderSendTab = () => (
    <Box sx={{ p: 3 }}>
      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Notification Content */}
        <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Edit />
                  Bildirim İçeriği
                </Box>
              }
            />
            <CardContent sx={{ space: 2 }}>
              {/* Templates */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Hızlı Şablonlar
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(templates).map(([key, template]) => (
                    <Button
                      key={key}
                      variant="outlined"
                      size="small"
                      onClick={() => applyTemplate(key)}
                    >
                      {template.title}
                    </Button>
                  ))}
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Başlık"
                value={notification.title}
                onChange={(e) => setNotification(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Bildirim başlığı"
                inputProps={{ maxLength: 100 }}
                helperText={`${notification.title.length}/100`}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Mesaj"
                value={notification.body}
                onChange={(e) => setNotification(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Bildirim mesajı"
                inputProps={{ maxLength: 500 }}
                helperText={`${notification.body.length}/500`}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Görsel URL (İsteğe bağlı)"
                value={notification.imageUrl || ''}
                onChange={(e) => setNotification(prev => ({ ...prev, imageUrl: e.target.value }))}
                placeholder="https://example.com/image.jpg"
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Bildirim Türü</InputLabel>
                <Select
                  value={notificationData.type}
                  label="Bildirim Türü"
                  onChange={(e) => {
                    const type = e.target.value;
                    setNotificationData(prev => ({
                      ...prev,
                      type: type,
                      // Kampanya türü seçilirse varsayılan bir deepLink taslağı hazırla
                      deepLink: type === 'premium_campaign' ? `coffy://premium?campaign=${prev.campaign || ''}` : prev.deepLink
                    }));
                  }}
                >
                  <MenuItem value="general">Genel</MenuItem>
                  <MenuItem value="match">Eşleşme</MenuItem>
                  <MenuItem value="message">Mesaj</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="premium_campaign">Kampanya (İndirim)</MenuItem>
                  <MenuItem value="event">Etkinlik</MenuItem>
                  <MenuItem value="system">Sistem</MenuItem>
                </Select>
              </FormControl>

              {notificationData.type === 'premium_campaign' && (
                <TextField
                  fullWidth
                  label="Kampanya Etiketi (RevenueCat Tag)"
                  value={notificationData.campaign || ''}
                  onChange={(e) => {
                    const tag = e.target.value;
                    setNotificationData(prev => ({
                      ...prev,
                      campaign: tag,
                      deepLink: `coffy://premium?campaign=${tag}`
                    }));
                  }}
                  placeholder="örn: yaz_indirimi_50"
                  sx={{ mb: 2 }}
                  helperText="Bu etiket RevenueCat'teki offering ID veya kampanya etiketi ile aynı olmalıdır."
                />
              )}

              {/* Scheduling */}
              <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  <Schedule sx={{ mr: 1 }} />
                  Zamanlama
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isScheduled}
                      onChange={(e) => setIsScheduled(e.target.checked)}
                    />
                  }
                  label="Zamanlanmış gönderim"
                />

                {isScheduled && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <TextField
                      type="date"
                      label="Tarih"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    />
                    <TextField
                      type="time"
                      label="Saat"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Preview */}
          {(notification.title || notification.body) && renderNotificationPreview()}
        </Box>

        {/* Send Options */}
        <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Single User */}
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main', mx: 'auto', mb: 2 }}>
                  <Person sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Tek Kullanıcı
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Belirli bir kullanıcıya gönder
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Kullanıcı Seç</InputLabel>
                  <Select
                    value={selectedUserId}
                    label="Kullanıcı Seç"
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    {users.length > 0 ? (
                      users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.fullName || user.username || user.email || user.id}
                          {user.email && user.fullName && ` (${user.email})`}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled value="">
                        Kullanıcı bulunamadı
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={sendToUser}
                  disabled={loading || !selectedUserId || !notification.title}
                  startIcon={<Send />}
                >
                  Gönder
                </Button>

              </CardContent>
            </Card>

            {/* All Users */}
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
                  <Broadcast sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Tüm Kullanıcılar
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Tüm aktif kullanıcılara gönder
                </Typography>

                <Typography variant="h4" color="success.main" sx={{ mb: 2 }}>
                  {filterOptions?.userCounts.total || 0}
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={sendToAll}
                  disabled={loading || !notification.title}
                  startIcon={<People />}
                >
                  Toplu Gönder
                </Button>
              </CardContent>
            </Card>

            {/* Filtered */}
            <Card>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'secondary.main', mx: 'auto', mb: 2 }}>
                  <MyLocation sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Filtreli Gönderim
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Belirli kriterlere uygun kullanıcılara
                </Typography>

                <Typography variant="h4" color="secondary.main" sx={{ mb: 2 }}>
                  ~{estimatedReach}
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowFilterDialog(true)}
                  startIcon={<FilterList />}
                  sx={{ mb: 2 }}
                >
                  Filtre Ayarla
                </Button>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={sendFiltered}
                  disabled={loading || !notification.title}
                  startIcon={<MyLocation />}
                >
                  Filtreli Gönder
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>



      {/* Filter Dialog */}
      <Dialog open={showFilterDialog} onClose={() => setShowFilterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Filtre Ayarları</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Cinsiyet</InputLabel>
              <Select
                value={filters.gender}
                label="Cinsiyet"
                onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value as any }))}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="male">Erkek</MenuItem>
                <MenuItem value="female">Kadın</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label="Min Yaş"
                value={filters.minAge || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, minAge: parseInt(e.target.value) }))}
                inputProps={{ min: 18, max: 100 }}
              />
              <TextField
                type="number"
                label="Max Yaş"
                value={filters.maxAge || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, maxAge: parseInt(e.target.value) }))}
                inputProps={{ min: 18, max: 100 }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Şehir</InputLabel>
              <Select
                value={filters.city || 'all'}
                label="Şehir"
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value === 'all' ? undefined : e.target.value }))}
              >
                <MenuItem value="all">Tüm şehirler</MenuItem>
                {filterOptions?.cities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Premium Durumu</InputLabel>
              <Select
                value={filters.isPremium === undefined ? 'all' : filters.isPremium.toString()}
                label="Premium Durumu"
                onChange={(e) => setFilters(prev => ({ ...prev, isPremium: e.target.value === 'all' ? undefined : e.target.value === 'true' }))}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="true">Premium</MenuItem>
                <MenuItem value="false">Ücretsiz</MenuItem>
              </Select>
            </FormControl>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Tahmini Erişim
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {estimatedReach} kullanıcı
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFilterDialog(false)}>İptal</Button>
          <Button onClick={() => { setShowFilterDialog(false); sendFiltered(); }} variant="contained">
            Filtreli Gönder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderStatsTab = () => (
    <Box sx={{ p: 3 }}>
      {stats && (
        <>
          {/* Overview Cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <NotificationsActive sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" gutterBottom>{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">Toplam Bildirim</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" color="success.main" gutterBottom>{stats.successful}</Typography>
                  <Typography variant="body2" color="text.secondary">Başarılı</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Cancel sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                  <Typography variant="h4" color="error.main" gutterBottom>{stats.failed}</Typography>
                  <Typography variant="body2" color="text.secondary">Başarısız</Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <BarChart sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" color="info.main" gutterBottom>
                    {stats.total > 0 ? Math.round((stats.successful / stats.total) * 100) : 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Başarı Oranı</Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Type Distribution */}
          <Card>
            <CardHeader title="Bildirim Türleri" />
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">{stats.byType.single}</Typography>
                  <Typography variant="body2" color="text.secondary">Tekil</Typography>
                </Box>
                <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">{stats.byType.broadcast}</Typography>
                  <Typography variant="body2" color="text.secondary">Toplu</Typography>
                </Box>
                <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main">{stats.byType.filtered}</Typography>
                  <Typography variant="body2" color="text.secondary">Filtreli</Typography>
                </Box>
                <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">{stats.byType.scheduled}</Typography>
                  <Typography variant="body2" color="text.secondary">Zamanlanmış</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );

  const renderHistoryTab = () => (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <History />
              Bildirim Geçmişi
            </Box>
          }
        />
        <CardContent>
          {history.length > 0 ? (
            <List>
              {history.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {item.status === 'sent' || item.status === 'completed' ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.status === 'sent' || item.status === 'completed' ? 'Başarılı' : 'Başarısız'}
                            color={item.status === 'sent' || item.status === 'completed' ? 'success' : 'error'}
                          />
                          <Chip
                            size="small"
                            label={
                              item.type === 'single' ? 'Tekil' :
                                item.type === 'broadcast' ? 'Toplu' :
                                  item.type === 'filtered' ? 'Filtreli' : 'Zamanlanmış'
                            }
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {item.notification.body}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Gönderen: {item.sentBy} • {new Date(item.sentAt).toLocaleString('tr-TR')}
                            {item.results && (
                              <> • Başarılı: {item.results.successful || 0}, Başarısız: {item.results.failed || 0}</>
                            )}
                          </Typography>
                          {item.error && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                              Hata: {item.error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < history.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsActive sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Henüz bildirim geçmişi bulunmuyor
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderScheduledTab = () => (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule />
              Zamanlanmış Bildirimler
            </Box>
          }
        />
        <CardContent>
          {scheduled.length > 0 ? (
            <List>
              {scheduled.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {item.status === 'scheduled' ? (
                        <Schedule color="primary" />
                      ) : item.status === 'sent' ? (
                        <CheckCircle color="success" />
                      ) : item.status === 'cancelled' ? (
                        <Cancel color="warning" />
                      ) : (
                        <Cancel color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              item.status === 'scheduled' ? 'Bekliyor' :
                                item.status === 'sent' ? 'Gönderildi' :
                                  item.status === 'cancelled' ? 'İptal' : 'Başarısız'
                            }
                            color={
                              item.status === 'scheduled' ? 'primary' :
                                item.status === 'sent' ? 'success' :
                                  item.status === 'cancelled' ? 'warning' : 'error'
                            }
                          />
                          <Chip
                            size="small"
                            label={
                              item.type === 'single' ? 'Tekil' :
                                item.type === 'broadcast' ? 'Toplu' : 'Filtreli'
                            }
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {item.notification.body}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Oluşturan: {item.createdBy} • Zamanlanmış: {new Date(item.scheduledFor).toLocaleString('tr-TR')}
                          </Typography>
                          {item.error && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                              Hata: {item.error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {item.status === 'scheduled' && (
                      <IconButton
                        edge="end"
                        onClick={() => cancelScheduled(item.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </ListItem>
                  {index < scheduled.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Schedule sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Zamanlanmış bildirim bulunmuyor
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ mb: 3 }}>
        <Toolbar sx={{ px: 0 }}>
          <NotificationsActive sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Push Bildirimler
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Kullanıcılara bildirim gönder ve yönet
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            label="Gönder"
            icon={<Send />}
            iconPosition="start"
          />
          <Tab
            label="İstatistikler"
            icon={<BarChart />}
            iconPosition="start"
          />
          <Tab
            label="Geçmiş"
            icon={<History />}
            iconPosition="start"
          />
          <Tab
            label="Zamanlanmış"
            icon={<Schedule />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && renderSendTab()}
      {activeTab === 1 && renderStatsTab()}
      {activeTab === 2 && renderHistoryTab()}
      {activeTab === 3 && renderScheduledTab()}
    </Container>
  );
};

export default NotificationsPage;